import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { authService } from '../services/auth'
import { profileService } from '../services/profile'
import { setCallerSellerId } from '../crm/services/api'
import pageCache, { navState } from '../crm/services/pageCache'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [initializing, setInitializing] = useState(true)

  useEffect(() => {
    // onAuthStateChange emite INITIAL_SESSION imediatamente ao se inscrever —
    // nascente única da sessão. init()/getSession() separado criava dois rios
    // disputando setUser e podia deixar initializing=true para sempre se falhasse.
    const { data: { subscription } } = authService.onAuthStateChange(
      async (_event, session) => {
        const currentUser = session?.user || null
        setUser(currentUser)

        if (currentUser) {
          try {
            const userProfile = await profileService.getProfile(currentUser.id)
            setProfile(userProfile)
          } catch {
            setProfile(null)
          }
        } else {
          setProfile(null)
        }

        setInitializing(false) // 1ª emissão (INITIAL_SESSION) abre o portão
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const login = useCallback(async (email, password) => {
    setError('')
    setLoading(true)

    try {
      await authService.signIn(email, password)
      // onAuthStateChange (SIGNED_IN) cuida de setUser + setProfile.
      // login() tem uma responsabilidade: autenticar e navegar.
      navigate('/app')
    } catch (err) {
      setError(err.message === 'Invalid login credentials'
        ? 'Email ou senha incorretos'
        : err.message)
    } finally {
      setLoading(false)
    }
  }, [navigate])

  const logout = useCallback(async () => {
    // Limpa o cache de página antes de deslogar.
    // Garante que dados do usuário atual não vazem para o próximo
    // usuário que logar no mesmo browser/computador.
    pageCache.clear()
    navState.clear(profile?.seller_id)
    await authService.signOut()
    setProfile(null)
    navigate('/login')
  }, [navigate])

  const isAuthenticated = useCallback(() => {
    return !!user
  }, [user])

  // Sincroniza identidade do caller para o api.js (regra de privacidade no backend).
  useEffect(() => {
    setCallerSellerId(profile?.seller_id || null)
  }, [profile?.seller_id])

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      login,
      logout,
      isAuthenticated,
      loading,
      error,
      initializing,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>')
  return ctx
}
